// cruiseNames — guest-facing cruise names for the documents a customer receives.
//
// cruise_settings.cruise_name is the join key for bookings, invoices, pricing,
// seating and every RPC/trigger, and never changes. What a GUEST sees is
// cruise_settings.display_name (long) and display_name_short (constrained
// spots, and every WhatsApp template variable). NULL means "use cruise_name",
// so a cruise with no display name shows its stored name and there is no name
// table in code. Migration: alfresco-grand-web
// supabase/migrations/20261008120000_cruise_display_names.sql.
//
// Same contract as the web repo's pair, kept in step by hand:
//   alfresco-grand-web/src/components/shared/helpers.js      cruiseLabel()
//   alfresco-grand-web/src/lib/cruiseDetails.js              fetchCruiseNames()
//   alfresco-grand-web/supabase/functions/_shared/cruiseLabel.ts (edge functions)
//
// Never pass a label into a query, a booking or invoice write, or anything
// logic keys on — those take the stored cruise_name. Staff screens keep the
// stored name; only the ticket PDF, the HTML ticket, the invoice and credit
// note prints, and the send-ticket template variable read these.

import { supabase } from '../lib/supabase';

const NAME_COLUMNS = 'cruise_name, display_name, display_name_short';

/**
 * The guest-facing label for a cruise_settings row.
 * @param {{ cruise_name?: string, display_name?: string|null, display_name_short?: string|null } | null} cruise
 * @param {'long'|'short'} form
 * @returns {string} '' when no row, so callers fall back to the stored name they hold.
 */
export function cruiseLabel(cruise, form = 'long') {
  if (!cruise) return '';
  const stored = cruise.cruise_name ?? '';
  const chosen = form === 'short' ? cruise.display_name_short : cruise.display_name;
  const label = typeof chosen === 'string' ? chosen.trim() : '';
  return label || stored;
}

/**
 * The names row for one cruise, by stored cruise_name (bookings.cruise_type).
 * null when not found or on error, so cruiseLabel(null) yields '' and the
 * caller falls back to the stored name it already holds.
 * @returns {Promise<{ cruise_name: string, display_name: string|null, display_name_short: string|null } | null>}
 */
export async function fetchCruiseNames({ cruiseName }) {
  if (!cruiseName) return null;
  const { data, error } = await supabase
    .from('cruise_settings')
    .select(NAME_COLUMNS)
    .eq('cruise_name', cruiseName)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}
